import {
  CompletionTargetUnit,
  FrequencyUnit,
  HabitSchedule,
  HabitScheduleTimesPer,
  RruleReferenceDate,
  TemplateSchedule,
  TranslateFunction,
  WeekDays,
  weekendDays,
  workWeek,
} from '@griply/types';

import {
  formatRruleWeekDays,
  getOrdinalWeekDays,
} from './format-rrule-week-days';
import { Frequency, RRule } from './lib/rrule';

/**
 * Minimal schedule shape accepted by `getScheduleLabel`. Compatible with
 * both `HabitSchedule` and task-template schedule shapes — anything with an
 * rrule format, an optional frequency, and a completion target works.
 */
export type GetScheduleLabelSchedule = {
  rrule: {
    format: string;
    referenceDate: RruleReferenceDate;
  };
  frequency?: {
    count: number;
    unit: FrequencyUnit;
    label?: string | null;
  } | null;
  completionTarget?: {
    unit: CompletionTargetUnit;
    count: number;
  };
};

const rruleScheduleLabel = (
  schedule: HabitSchedule | TemplateSchedule,
  t: TranslateFunction,
) => {
  const rrule = RRule.fromString(schedule.rrule.format);
  const weekDays = formatRruleWeekDays(schedule.rrule.format);

  // yearly
  if (rrule.origOptions.freq === Frequency.YEARLY) {
    if (rrule.origOptions.byweekday && !weekDays.length) {
      // every year on the 1st mon
      const [{ day, ordinal }] = getOrdinalWeekDays(
        rrule.origOptions.byweekday,
      );
      // multiple days logic
      // const days = getOrdinalWeekDays(rrule.origOptions.byweekday);
      // dayLabel = days
      //   .map(({ day, ordinal }) => {
      //     const oLabel = t(
      //       `general.labels.habit.repeat.value.ordinal.specific-day.${ordinal}.label.short`,
      //     );
      //     const dLabel = t(
      //       `general.labels.habit.repeat.value.specific-days.days.${day}.label`,
      //     );
      //     return `${oLabel} ${dLabel}`;
      //   })
      //   .join(', ');
      // dayLabel = [
      //   ...(Array.isArray(rrule.origOptions.byweekday)
      //     ? rrule.origOptions.byweekday
      //     : [rrule.origOptions.byweekday]),
      // ]
      //   .map((weekDay) => weekDay.toString())
      //   .join(', ');

      return t('general.labels.habit.repeat.value.per-year.label', {
        count: rrule.origOptions.interval ?? 1,
        ordinalNumber: t(
          `general.labels.habit.repeat.value.ordinal.specific-day.${ordinal}.label.short`,
        ),
        day: t(
          `general.labels.habit.repeat.value.specific-days.days.${day}.label`,
        ),
      });
    }

    let dayLabel = 'day';
    if (weekDays.length === 1) {
      dayLabel = weekDays[0];
    }

    if (
      weekDays.length === workWeek.length &&
      workWeek.every((weekDay) => weekDays.includes(weekDay))
    ) {
      dayLabel = 'work-day';
    }

    if (
      weekDays.length === weekendDays.length &&
      weekendDays.every((weekendDay) => weekDays.includes(weekendDay))
    ) {
      dayLabel = 'weekend';
    }

    if (rrule.origOptions.bymonthday) {
      const days = [
        ...(Array.isArray(rrule.origOptions.bymonthday)
          ? rrule.origOptions.bymonthday
          : [rrule.origOptions.bymonthday]),
      ]
        .sort((monthDayA, monthDayB) => monthDayA - monthDayB)
        .map((monthDay) => t(`general.labels.${monthDay}.ordinal`))
        .join(', ');

      return t(
        'general.labels.habit.repeat.value.per-year.on-day-of-month.label',
        {
          count: rrule.origOptions.interval ?? 1,
          day: days,
          month: t(
            `general.labels.habit.repeat.year.months.${rrule.origOptions.bymonth}.label.short`,
          ),
        },
      );
    }

    if (rrule.origOptions.bymonth) {
      return t(
        'general.labels.habit.repeat.value.per-year.on-ordinal-day-of-month.label',
        {
          count: rrule.origOptions.interval ?? 1,
          ordinalNumber: t(
            `general.labels.habit.repeat.value.ordinal.specific-day.${rrule.origOptions.bysetpos}.label.short`,
          ),
          day: t(
            `general.labels.habit.repeat.value.specific-days.days.${dayLabel}.label`,
          ),
          month: t(
            `general.labels.habit.repeat.year.months.${rrule.origOptions.bymonth}.label.short`,
          ),
        },
      );
    }

    return t('general.labels.habit.repeat.value.per-year.label', {
      count: rrule.origOptions.interval ?? 1,
      ordinalNumber: t(
        `general.labels.habit.repeat.value.ordinal.specific-day.${rrule.origOptions.bysetpos}.label.short`,
      ),
      day: t(
        `general.labels.habit.repeat.value.specific-days.days.${dayLabel}.label`,
      ),
    });
  }
  // monthly
  if (rrule.origOptions.freq === Frequency.MONTHLY) {
    if (rrule.origOptions.bysetpos) {
      let dayLabel = 'day';
      if (weekDays.length === 1) {
        dayLabel = weekDays[0];
      }

      if (
        weekDays.length === workWeek.length &&
        workWeek.every((weekDay) => weekDays.includes(weekDay))
      ) {
        dayLabel = 'work-day';
      }

      if (
        weekDays.length === weekendDays.length &&
        weekendDays.every((weekendDay) => weekDays.includes(weekendDay))
      ) {
        dayLabel = 'weekend';
      }

      return t(
        'general.labels.habit.repeat.value.specific-day.per-month.label',
        {
          count: rrule.origOptions.interval ?? 1,
          ordinalNumber: t(
            `general.labels.habit.repeat.value.ordinal.specific-day.${rrule.origOptions.bysetpos}.label.short`,
          ),
          day: t(
            `general.labels.habit.repeat.specific-days.days.${dayLabel}.label`,
          ),
        },
      );
    }

    if (rrule.origOptions.bymonthday === -1) {
      return t(
        'general.labels.habit.repeat.value.specific-day.per-month.label',
        {
          count: rrule.origOptions.interval ?? 1,
          ordinalNumber: t(
            `general.labels.habit.repeat.value.ordinal.specific-day.${rrule.origOptions.bymonthday}.label.short`,
          ),
          day: t('general.labels.habit.repeat.specific-days.days.day.label'),
        },
      );
    }

    if (rrule.origOptions.bymonthday) {
      const days = [
        ...(Array.isArray(rrule.origOptions.bymonthday)
          ? rrule.origOptions.bymonthday
          : [rrule.origOptions.bymonthday]),
      ]
        .sort((monthDayA, monthDayB) => monthDayA - monthDayB)
        .map((monthDay) => t(`general.labels.${monthDay}.ordinal`))
        .join(', ');

      return !!rrule.origOptions.interval && rrule.origOptions.interval > 1
        ? t('general.labels.habit.repeat.value.on-interval.label', {
            interval: rrule.origOptions.interval,
            unit: t(
              'general.labels.habit.repeat.value.interval.unit.month.short.label',
              {
                count: rrule.origOptions.interval,
              },
            ),
            specification: days,
          })
        : t(
            'general.labels.habit.repeat.value.specific-days.days.every.label',
            { day: days },
          );
    }

    if (rrule.origOptions.byweekday && !weekDays.length) {
      // every year on the 1st mon
      const [{ day, ordinal }] = getOrdinalWeekDays(
        rrule.origOptions.byweekday,
      );
      // multiple days logic
      // const days = getOrdinalWeekDays(rrule.origOptions.byweekday);
      // dayLabel = days
      //   .map(({ day, ordinal }) => {
      //     const oLabel = t(
      //       `general.labels.habit.repeat.value.ordinal.specific-day.${ordinal}.label.short`,
      //     );
      //     const dLabel = t(
      //       `general.labels.habit.repeat.value.specific-days.days.${day}.label`,
      //     );
      //     return `${oLabel} ${dLabel}`;
      //   })
      //   .join(', ');
      // dayLabel = [
      //   ...(Array.isArray(rrule.origOptions.byweekday)
      //     ? rrule.origOptions.byweekday
      //     : [rrule.origOptions.byweekday]),
      // ]
      //   .map((weekDay) => weekDay.toString())
      //   .join(', ');

      return t('general.labels.habit.repeat.value.per-month.label', {
        count: rrule.origOptions.interval ?? 1,
        ordinalNumber: t(
          `general.labels.habit.repeat.value.ordinal.specific-day.${ordinal}.label.short`,
        ),
        day: t(
          `general.labels.habit.repeat.value.specific-days.days.${day}.label`,
        ),
      });
    }
  }

  // check if it is set on every day
  if (weekDays.length === Object.keys(WeekDays).length) {
    return !!rrule.origOptions.interval && rrule.origOptions.interval > 1
      ? t('general.labels.habit.repeat.value.on-interval.label', {
          interval: rrule.origOptions.interval,
          unit: t(
            'general.labels.habit.repeat.value.interval.unit.week.short.label',
            {
              count: rrule.origOptions.interval,
            },
          ),
          specification: t(
            'general.labels.habit.repeat.value.specific-days.days.every-day.label',
          ),
        })
      : t(
          'general.labels.habit.repeat.value.specific-days.days.every-day.label',
        );
  }

  // check if it is a workweek
  if (
    workWeek.length === weekDays.length &&
    [...weekDays]
      .sort()
      .every((val, index) => val === [...workWeek].sort()[index])
  ) {
    return !!rrule.origOptions.interval && rrule.origOptions.interval > 1
      ? t('general.labels.habit.repeat.value.on-interval.label', {
          interval: rrule.origOptions.interval,
          unit: t(
            'general.labels.habit.repeat.value.interval.unit.week.short.label',
            {
              count: rrule.origOptions.interval,
            },
          ),
          specification: t(
            'general.labels.habit.repeat.value.specific-days.days.week-day.label',
            {
              count: 2,
            },
          ),
        })
      : t('general.labels.habit.repeat.value.specific-days.days.every.label', {
          day: t(
            'general.labels.habit.repeat.value.specific-days.days.week-day.label',
            {
              count: 1,
            },
          ),
        });
  }

  const days = weekDays.map((weekday) =>
    t(`general.labels.habit.repeat.value.specific-days.days.${weekday}.label`),
  );

  // check if it is set on a single day
  if (weekDays.length === 1) {
    return !!rrule.origOptions.interval && rrule.origOptions.interval > 1
      ? t('general.labels.habit.repeat.value.on-interval.label', {
          interval: rrule.origOptions.interval,
          unit: t(
            'general.labels.habit.repeat.value.interval.unit.week.short.label',
            {
              count: rrule.origOptions.interval,
            },
          ),
          specification: days[0],
        })
      : t('general.labels.habit.repeat.value.specific-days.days.every.label', {
          day: days[0],
        });
  }

  // map all days to specific format
  return !!rrule.origOptions.interval && rrule.origOptions.interval > 1
    ? t('general.labels.habit.repeat.value.on-interval.label', {
        interval: rrule.origOptions.interval,
        unit: t(
          'general.labels.habit.repeat.value.interval.unit.week.short.label',
          {
            count: rrule.origOptions.interval,
          },
        ),
        specification: days.join(', '),
      })
    : t('general.labels.habit.repeat.value.specific-days.days.every.label', {
        day: days.join(', '),
      });
};

const frequencyScheduleLabel = (
  schedule: HabitSchedule | TemplateSchedule,
  t: TranslateFunction,
) => {
  const frequency = (schedule as HabitScheduleTimesPer).frequency;
  // yearly
  if (frequency?.unit === FrequencyUnit.Year) {
    return t('general.labels.habit.repeat.value.target.per-year.label', {
      count: frequency.count,
      label:
        frequency.label ??
        t('general.labels.habit.repeat.value.target.default.label', {
          count: frequency.count,
        }),
    });
  }

  // monthly
  if (frequency?.unit === FrequencyUnit.Month) {
    return t('general.labels.habit.repeat.value.target.per-month.label', {
      count: frequency.count,
      label:
        frequency.label ??
        t('general.labels.habit.repeat.value.target.default.label', {
          count: frequency.count,
        }),
    });
  }

  // weekly
  return t('general.labels.habit.repeat.value.target.per-week.label', {
    count: frequency.count,
    label:
      frequency.label ??
      t('general.labels.habit.repeat.value.target.default.label', {
        count: frequency.count,
      }),
  });
};

export const getScheduleLabel = (
  schedule: HabitSchedule | TemplateSchedule,
  t: TranslateFunction,
  excludeTimesPerDay?: boolean,
) => {
  const frequency = (schedule as HabitScheduleTimesPer).frequency;
  const label = frequency
    ? frequencyScheduleLabel(schedule, t)
    : rruleScheduleLabel(schedule, t);
  const completionsPerDay = schedule.completionTarget?.count ?? 1;

  if (excludeTimesPerDay || completionsPerDay === 1) {
    return label;
  }

  if (
    label ===
    t('general.labels.habit.repeat.value.specific-days.days.every-day.label')
  ) {
    return t('general.labels.habit.repeat.value.times-per-day.label', {
      count: completionsPerDay,
    });
  }

  const decapitalizedLabel = label.charAt(0).toLowerCase() + label.slice(1);
  const timesPerLabel = t('general.labels.habit.repeat.value.times-per.label', {
    count: completionsPerDay,
  });

  return `${timesPerLabel} ${decapitalizedLabel}`;
};
